import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { AiChatbotInputMessageComponent, ChatMessageSubmit } from './ai-chatbot-input-message/ai-chatbot-input-message.component';
import { CommonModule } from '@angular/common';
import { AiApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/ai/ai-api';
import {
  WillyQuotaResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/ai/willy-quota.response';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { FileApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/file/file-api';
import {
  UploadFileRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/dms/upload-file-request';
import { FileType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/file-type.enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../ui-components/shared.module';
import { AiChatbotHeaderComponent } from './ai-chatbot-header/ai-chatbot-header.component';
import { OfflineBannerComponent } from '../../../ui-components/state/offline-banner.component';
import { GuestSignupCardComponent } from '../../../ui-components/guest-signup-card/guest-signup-card.component';
import { PremiumTeaserComponent } from '../../../ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../../../@toasts/premium-toast/premium-toast.component';
import { VwPluralPipe } from '../../../pipes/vw-plural.pipe';
import { CopyIconComponent } from '../../../../@icons/copy-icon.component';
import { InfoIconComponent } from '../../../../@icons/info-icon.component';
import { readPremiumRefusal } from '../../../@services/front/premium-error.util';
import { readWillyQuota, WillyQuotaView } from './willy-quota.util';

/** Limite gratuite, alignée sur `willy-quota.service.ts` du back. */
const WILLY_FREE_DAILY_LIMIT = 3;
import { PremiumService } from '../../../@services/front/premium.service';
import { StoreReviewService } from '../../../@services/front/store-review.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { StorageService } from '../../../@services/front/storage.service';
import { PosthogService } from '../../../@services/front/posthog.service';
import { WillyChatService } from '../../../@services/front/willy-chat.service';

/** One bubble of the visible thread. Error and welcome bubbles are kept out
 * of the history sent to the backend. */
interface ChatBubble {
  role: 'user' | 'assistant';
  text: string;
  photoUrls?: string[];
  externalFileUids?: string[];
  excludeFromHistory?: boolean;
  /** Une vraie réponse de Willy : elle se copie. */
  copyable?: boolean;
  /** Encart posé dans le fil, une fois : le diagnostic photo offert a servi (CR-05). */
  notice?: 'free-photo-used';
}

/** Source du paywall ouvert depuis le bouton photo verrouillé (CR-05). */
export const WILLY_PHOTO_PAYWALL_SOURCE = 'willy_photo';

@Component({
    selector: 'vw-ai-chatbot',
    imports: [
        AiChatbotInputMessageComponent,
        GuestSignupCardComponent,
        OfflineBannerComponent,
        CommonModule,
        TranslatePipe,
        SharedModule,
        AiChatbotHeaderComponent,
        PremiumTeaserComponent,
        PremiumToastComponent,
        VwPluralPipe,
        CopyIconComponent,
        InfoIconComponent,
    ],
    providers: [
        {
            provide: AiApi,
            useClass: AiApi,
        },
        {
            provide: AuthApi,
            useClass: AuthApi,
        },
        {
            provide: FileApi,
            useClass: FileApi,
        },
    ],
    templateUrl: './ai-chatbot.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './ai-chatbot.component.scss'
})
export class AiChatbotComponent implements OnDestroy {
  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  @Output() inputFocus = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();
  /** Le chevron de l'en-tête : la feuille qui héberge Willy se referme. */
  @Output() closeRequested = new EventEmitter<void>();

  @ViewChild(AiChatbotInputMessageComponent) inputMessage: AiChatbotInputMessageComponent;
  @ViewChild('thread') thread: ElementRef<HTMLDivElement>;

  inputIsFocus = false;
  chatSessionId: string | undefined;
  messages: ChatBubble[] = [];
  willyLoading = false;
  isGuest = false;

  readonly premiumService = inject(PremiumService);
  /** Avis des magasins après la 3e réponse de Willy (CR-06). */
  private readonly storeReview = inject(StoreReviewService);
  premiumPaywallOpen = false;
  /** D'où le paywall a été ouvert : son titre et son premier avantage en dépendent. */
  premiumPaywallSource = 'willy_chat';

  /**
   * Le dernier envoi n'a pas abouti (panne, réseau) : clé du message affiché
   * sous le fil, avec « Réessayer ». Le texte est rendu au champ.
   */
  sendError: string | null = null;
  private failedSubmit: ChatMessageSubmit | null = null;

  /** Sans réseau, rien ne peut partir : la saisie se ferme et le dit. */
  isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

  /** Index de la réponse qui vient d'être copiée, le temps de le dire. */
  copiedIndex: number | null = null;

  /**
   * Décompte du jour, lu au serveur.
   *
   * `null` tant qu'il n'est pas connu, ou si l'appel échoue : ne pas savoir
   * combien de questions restent ne doit jamais empêcher d'en poser une.
   */
  willyQuota: WillyQuotaResponse | null = null;

  /** Ce que le décompte autorise. Les règles vivent dans `willy-quota.util`. */
  private get quotaView(): WillyQuotaView {
    return readWillyQuota(this.willyQuota, this.premiumService.hasPremiumAccess());
  }

  /** Ligne du décompte sous la saisie : après la première question, et au mur. */
  get showQuotaLine(): boolean {
    return this.quotaView.showChip || this.quotaView.exhausted;
  }

  /** Questions encore disponibles aujourd'hui. */
  get quotaRemaining(): number {
    return this.quotaView.remaining;
  }

  /** Le quota du jour est épuisé pour un compte gratuit. */
  get isQuotaExhausted(): boolean {
    return this.quotaView.exhausted;
  }

  /**
   * Le diagnostic photo offert a servi (CR-05) : le bouton photo porte un
   * cadenas et ouvre l'offre. Sans décompte connu, ou face à une API qui ne
   * le dit pas encore, on laisse faire : le serveur tranchera.
   */
  get photoLocked(): boolean {
    return !this.premiumService.hasPremiumAccess() && this.willyQuota?.freePhotoAvailable === false;
  }

  /** Texte de la dernière vraie réponse de Willy (pas l'accueil), pour un signalement. */
  get lastAnswer(): string | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const m = this.messages[i];
      if (m.role === 'assistant' && m.copyable) return m.text;
    }
    return null;
  }

  /** La saisie est fermée : hors réseau, ou mur du jour atteint. */
  get composerDisabled(): boolean {
    return this.isOffline || this.isQuotaExhausted;
  }

  /** Le champ vide dit pourquoi il est fermé. */
  get composerPlaceholder(): string {
    if (this.isOffline) return 'willy.offline-placeholder';
    if (this.isQuotaExhausted) return 'willy.wall.placeholder';
    return 'willy.placeholder';
  }

  private openChatSubscription: Subscription;

  constructor(
    private aiApi: AiApi,
    private authApi: AuthApi,
    private fileApi: FileApi,
    private translate: TranslateService,
    private posthogService: PosthogService,
    private willyChatService: WillyChatService,
  ) { }

  async ngOnInit() {
    await this.pushWelcomeMessage();

    // Guest check from the token, without fetching user data (no group claim = not a guest)
    this.isGuest = this.authApi.isGuest();

    // Un invité n'a pas de compteur : il n'a pas encore de compte.
    if (!this.isGuest) void this.loadQuota();

    // Pre-fill the input when the chat is opened from elsewhere (e.g. plant details)
    this.openChatSubscription = this.willyChatService.openChat$.subscribe((prefillText) => {
      if (prefillText) {
        this.inputMessage?.setMessage(prefillText);
      }
    });
  }

  ngOnDestroy() {
    this.openChatSubscription?.unsubscribe();
    this.revokePhotoUrls();
  }

  /**
   * La feuille vient de s'ouvrir : on relit le décompte. Le chat vit aussi
   * longtemps que l'app ; un mur atteint hier soir ne doit pas fermer la
   * saisie ce matin.
   */
  onOpened() {
    if (!this.isGuest) void this.loadQuota();
  }

  @HostListener('window:online')
  onOnline() {
    this.isOffline = false;
  }

  @HostListener('window:offline')
  onOffline() {
    this.isOffline = true;
  }

  /** « Réessayer » du bandeau hors ligne : relit l'état du réseau. */
  checkConnection() {
    this.isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  /** « Réessayer » : renvoie le message qui n'est pas parti, photos comprises. */
  async retry() {
    const submit = this.failedSubmit;
    if (!submit) return;
    this.inputMessage?.resetMessage();
    await this.sendMessageToWilly(submit);
  }

  /** Ouvre le paywall en disant d'où : le premier avantage montré en dépend. */
  openPaywall(source: string) {
    this.premiumPaywallSource = source;
    this.premiumPaywallOpen = true;
  }

  /** Photo verrouillée touchée : l'offre, et rien d'autre (un seul mur). */
  onPhotoLocked() {
    this.posthogService.capture('willy_photo_locked_clicked');
    this.openPaywall(WILLY_PHOTO_PAYWALL_SOURCE);
  }

  /** « Copier » sous une réponse : le texte part dans le presse-papiers. */
  async copy(bubble: ChatBubble, index: number) {
    try {
      await navigator.clipboard.writeText(bubble.text);
      this.copiedIndex = index;
      setTimeout(() => {
        if (this.copiedIndex === index) this.copiedIndex = null;
      }, 2000);
    } catch (e) {
      console.error('AiChatbotComponent: clipboard unavailable', e);
    }
  }

  // Send the message (and optional photos) to the AI and append the reply to the thread
  async sendMessageToWilly(submit: ChatMessageSubmit) {
    if (this.willyLoading) {
      return;
    }
    this.sendError = null;
    this.failedSubmit = null;

    // Quota épuisé : inutile d'envoyer la question pour se faire refuser, et
    // d'empiler un second message identique dans le fil. On revérifie tout de
    // même auprès du serveur — le compteur local peut dater d'hier, ou d'un
    // abonnement souscrit entre-temps — et on n'ouvre l'offre que s'il confirme.
    if (this.isQuotaExhausted) {
      await this.loadQuota();
      if (this.isQuotaExhausted) {
        this.openPaywall('willy_quota_reached');
        return;
      }
    }

    this.posthogService.capture('willy_message_sent');

    // Le diagnostic offert va servir : on le saura consommé à la réponse.
    const usesFreePhoto =
      !!submit.photos?.length &&
      !this.premiumService.hasPremiumAccess() &&
      this.willyQuota?.freePhotoAvailable === true;

    // History BEFORE this turn — the new question travels as `text`
    const history = this.messages
      .filter((m) => !m.excludeFromHistory && m.text)
      .map((m) => ({ role: m.role, text: m.text, externalFileUids: m.externalFileUids }))
      .slice(-20);

    const userBubble: ChatBubble = {
      role: 'user',
      text: submit.text,
      photoUrls: submit.photos?.map((photo) => URL.createObjectURL(photo)),
    };
    this.messages.push(userBubble);
    this.willyLoading = true;
    this.scrollToBottom();

    try {
      let externalFileUids: string[] | undefined;
      let text = submit.text;
      if (submit.photos?.length) {
        this.posthogService.capture('willy_message_with_photo', { count: submit.photos.length });
        try {
          externalFileUids = await Promise.all(submit.photos.map((photo) => this.uploadPhoto(photo)));
          userBubble.externalFileUids = externalFileUids;
        } catch {
          // S3 upload failure is not a Willy failure: dedicated message
          this.failSend(userBubble, submit, 'willy.photo.error.upload');
          return;
        }
        if (!text || text.length === 0) {
          text = await firstValueFrom(this.translate.get('willy.photo.default-question'));
          userBubble.text = text;
        }
      }
      const willyResponse = await this.aiApi.chatWithWilly({
        text,
        sessionId: this.chatSessionId,
        externalFileUids,
        history,
      });
      // The vision branch has no Bedrock session: keep the current one alive
      this.chatSessionId = willyResponse.sessionId ?? this.chatSessionId;
      this.messages.push({ role: 'assistant', text: willyResponse.text, copyable: true });
      void this.storeReview.afterWillyAnswer();

      // Une fois, après la réponse : le cadeau est dit au moment où il a servi.
      if (usesFreePhoto && this.willyQuota) {
        this.willyQuota.freePhotoAvailable = false;
        this.messages.push({ role: 'assistant', text: '', notice: 'free-photo-used', excludeFromHistory: true });
      }

      // Le serveur vient de consommer une question : on suit son décompte sans
      // le rappeler. Un aller-retour par message pour un chiffre qu'on connaît
      // déjà serait du gaspillage.
      if (this.willyQuota) this.willyQuota.used += 1;
    } catch (error: any) {
      // A failed turn must not pollute the history (a stale user turn would
      // make the backend skip the personalized context for ever after)
      userBubble.excludeFromHistory = true;

      // Un refus premium n'est PAS une erreur : ni message rouge, ni « oups ».
      // Le quota atteint ouvre l'offre ; l'usage équitable, jamais — la
      // personne a déjà payé pour de l'illimité.
      const refusal = readPremiumRefusal(error);

      // Le diagnostic offert a déjà servi (l'écran ne le savait pas encore) :
      // la question n'est pas partie. Elle revient dans le champ, l'offre s'ouvre.
      if (refusal?.code === 'WILLY_PHOTO_LOCKED') {
        this.removeBubble(userBubble);
        this.inputMessage?.setMessage(submit.text);
        if (this.willyQuota) this.willyQuota.freePhotoAvailable = false;
        this.openPaywall(WILLY_PHOTO_PAYWALL_SOURCE);
        return;
      }

      // Le mur du jour : la carte du mur, dans le fil, le dit. Pas de bulle en
      // plus, pas de plein écran surgi par-dessus.
      if (refusal?.code === 'WILLY_QUOTA_EXCEEDED') {
        const limit = refusal.limit ?? WILLY_FREE_DAILY_LIMIT;
        this.willyQuota = { ...this.willyQuota, used: limit, limit, unlimited: false };
        return;
      }

      if (refusal?.messageKey) {
        await this.pushErrorMessage(refusal.messageKey, { limit: refusal.limit ?? WILLY_FREE_DAILY_LIMIT });

        if (refusal.shouldOpenPaywall) {
          this.premiumPaywallOpen = true;
        }
        return;
      }

      let errorKey = 'willy.error.failed';
      switch (error.data?.statusCode) {
        case 403:
          errorKey = 'willy.error.403';
          break;
        case 429:
          errorKey = 'willy.error.429';
          break;
      }
      this.failSend(userBubble, submit, errorKey);
    } finally {
      this.willyLoading = false;
      this.scrollToBottom();
    }
  }

  /**
   * Relit le décompte du jour.
   *
   * Un échec laisse `willyQuota` à `null` : ni décompte, ni mur. Le serveur
   * refusera de lui-même le moment venu, avec son message.
   */
  private async loadQuota(): Promise<void> {
    try {
      this.willyQuota = await this.aiApi.getWillyQuota();
    } catch {
      this.willyQuota = null;
    }
  }

  /**
   * L'envoi n'a pas abouti : la bulle sort du fil, le texte revient dans le
   * champ, et « Réessayer » renverra le message tel quel, photos comprises.
   */
  private failSend(bubble: ChatBubble, submit: ChatMessageSubmit, errorKey: string) {
    this.removeBubble(bubble);
    this.inputMessage?.setMessage(submit.text);
    this.failedSubmit = submit;
    this.sendError = errorKey;
  }

  private removeBubble(bubble: ChatBubble) {
    bubble.photoUrls?.forEach((url) => URL.revokeObjectURL(url));
    this.messages = this.messages.filter((m) => m !== bubble);
  }

  private async uploadPhoto(photo: File): Promise<string> {
    const uploadFileRequest: UploadFileRequest = new UploadFileRequest();
    uploadFileRequest.file = photo;
    uploadFileRequest.fileName = photo.name;
    uploadFileRequest.fileType = photo.type as FileType;
    const uploaded = await this.fileApi.uploadFile(uploadFileRequest);
    return uploaded.externalFileUid;
  }

  // "New conversation": forget the session and start from a blank thread
  async onNewConversation() {
    if (this.willyLoading) {
      return;
    }
    this.posthogService.capture('willy_conversation_reset');
    this.chatSessionId = undefined;
    this.sendError = null;
    this.failedSubmit = null;
    this.revokePhotoUrls();
    this.messages = [];
    await this.pushWelcomeMessage();
    this.inputMessage?.resetMessage();
  }

  private async pushWelcomeMessage() {
    this.messages.push({
      role: 'assistant',
      text: await firstValueFrom(this.translate.get('willy.hi')),
      excludeFromHistory: true,
    });
  }

  /**
   * Bulle de Willy hors conversation : erreur technique ou refus premium.
   *
   * `excludeFromHistory` est essentiel — un tour rate qui resterait dans
   * l'historique ferait sauter le contexte personnalise a tous les suivants.
   */
  private async pushErrorMessage(
    errorKey: string,
    params?: Record<string, unknown>,
  ) {
    this.messages.push({
      role: 'assistant',
      text: await firstValueFrom(this.translate.get(errorKey, params)),
      excludeFromHistory: true,
    });
  }

  private revokePhotoUrls() {
    this.messages.forEach((m) => m.photoUrls?.forEach((url) => URL.revokeObjectURL(url)));
  }

  private scrollToBottom() {
    setTimeout(() => {
      this.thread?.nativeElement?.scroll({
        top: this.thread.nativeElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 50);
  }

  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputFocus() {
    this.inputIsFocus = true;
    this.inputFocus.emit();
  }

  // Emiting input blur so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputBlur() {
    this.inputIsFocus = false;
    this.inputBlur.emit();
  }
}
